import { SyncStorage } from 'jotai/vanilla/utils/atomWithStorage';
import { deepEqual } from '../object';

interface Observer<T> {
  update(value: T): void;
}

interface Subject<T> {
  getValue(initialValue?: T | null): T | undefined | null;
  setValue(value: T | null): void;
  subscribe(observer: Observer<T>): void;
  unsubscribe(observer: Observer<T>): void;
}

type SubjectOptions = {
  failStrategy?: 'silent' | 'warn' | 'error';
};

class SubjectImpl<T> implements Subject<T> {
  private observers: Observer<T>[] = [];

  constructor(
    private value: T | null = null,
    private options: SubjectOptions = { failStrategy: 'error' }
  ) {}

  getValue(initialValue: T): T {
    return this.value || initialValue;
  }

  setValue(value: T): void {
    if (deepEqual(this.value, value)) return;
    this.value = value;
    if (
      this.options.failStrategy === 'silent' ||
      this.options.failStrategy === 'warn'
    ) {
      this.observers.forEach(o => {
        try {
          o.update(value);
        } catch (e) {
          if (this.options.failStrategy === 'warn') {
            console.warn(e);
          }
        }
      });
    } else {
      // fail the rest if one observer fails
      this.observers.forEach(o => o.update(value));
    }
  }

  subscribe(observer: Observer<T>): void {
    this.observers.push(observer);
  }

  unsubscribe(observer: Observer<T>): void {
    this.observers = this.observers.filter(o => o !== observer);
  }
}

const keyedSubjects: { [key: string]: Subject<any> } = {};

const jotaiInMemoryStorage: SyncStorage<any> = {
  getItem: (key: string, initialValue?: any) => {
    if (!keyedSubjects[key]) {
      keyedSubjects[key] = new SubjectImpl<any>();
    }
    return keyedSubjects[key].getValue(initialValue);
  },

  setItem: (key: string, newValue: any) => {
    if (!keyedSubjects[key]) {
      keyedSubjects[key] = new SubjectImpl<any>();
    }
    keyedSubjects[key].setValue(newValue);
  },

  removeItem: (key: string) => {
    keyedSubjects[key]?.setValue(null);
    delete keyedSubjects[key];
  },

  subscribe: (key: string, jotaiCallback, initialValue) => {
    if (!keyedSubjects[key]) {
      keyedSubjects[key] = new SubjectImpl<any>(initialValue);
    }

    const observer: Observer<any> = {
      update: jotaiCallback
    };

    keyedSubjects[key].subscribe(observer);

    return () => {
      keyedSubjects[key]?.unsubscribe(observer);
    };
  }
};

export default jotaiInMemoryStorage;
